import { Injectable } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { TrameHelper } from "../../shared/helpers/trame.helper";
import { TranslateService } from "@ngx-translate/core";
import { AttestationType } from "../../shared/enums/attestation-type.enum";
import { attestationTrameMapping } from "../../shared/mapping/attestation-trame-mapping";
import { FALSE, TRUE } from "../constants/general.constants";

@Injectable({
  providedIn: "root",
})
export class ContactFormService {
  readonly selectedReference: string = "selectedReference";
  readonly selectedRequestNature: string = "selectedRequestNature";
  readonly selectedTerminationReason: string = "selectedTerminationReason";
  readonly selectedTerminationDate: string = "selectedTerminationDate";
  readonly selectedAttestationType: string = "selectedAttestationType";
  readonly isAttestationFor: string = "isAttestationFor";
  readonly comment: string = "comment";

  constructor(
    private readonly fb: FormBuilder,
    private readonly translate: TranslateService
  ) {}

  initializeForm(): FormGroup {
    return this.fb.group({
      selectedReference: ["", Validators.required],
      selectedRequestNature: [{ value: "", disabled: true }, Validators.required],
      selectedTerminationReason: [""],
      selectedTerminationDate: [""],
      selectedAttestationType: [""],
      isAttestationFor: [{ value: true, disabled: false }],
      comment: [{ value: "", disabled: true }],
    });
  }

  resetFormControls(form: FormGroup, controlNames: string[]) {
    controlNames.forEach(controlName => {
      const control = form.get(controlName);
      if (control) {
        control.setValue("");
        control.clearValidators();
        control.updateValueAndValidity();
      }
    });
  }

  intializeController(form: FormGroup, controlNames: string) {
    form.get(controlNames)?.enable();
    form.get(controlNames)?.setValue("");
  }

  setValidator(form: FormGroup, controlName: string, validators: any[]) {
    const control = form.get(controlName);
    if (control) {
      control.setValidators(validators);
      control.updateValueAndValidity();
    }
  }

  setValidators(form: FormGroup, controlNames: string[], validators: any[]) {
    controlNames.forEach(controlName => {
      const control = form.get(controlName);
      if (control) {
        control.setValidators(validators);
        control.updateValueAndValidity();
      }
    });
  }

  enableControl(form: FormGroup, controlName: string) {
    const control = form.get(controlName);
    if (control) {
      control.enable();
      control.updateValueAndValidity();
    }
  }

  updateAttestationTrame(form: FormGroup) {
    const attestationControl = form.get(this.selectedAttestationType);
    const insuredControl = form.get(this.isAttestationFor);
    const commentControl = form.get(this.comment);

    if (!attestationControl || !insuredControl || !commentControl) {
      console.error("One of the required controls is missing.");
      return;
    }

    const attestationType = attestationControl.value as AttestationType;
    const insuredKey = insuredControl.value === true ? TRUE : insuredControl.value === false ? FALSE : undefined;

    let trame = "";
    if (attestationType && insuredKey && attestationTrameMapping[attestationType]?.[insuredKey]) {
      trame = this.translate.instant(attestationTrameMapping[attestationType][insuredKey]) || "";
    }

    if (trame && trame.trim() !== "") {
      commentControl.setValue(trame);

      commentControl.setValidators([Validators.required, TrameHelper.validateTrameCompletion(trame)]);
    } else {
      commentControl.setValue("");
      commentControl.clearValidators();
    }

    commentControl.updateValueAndValidity();
  }

  getTrame(form: FormGroup): string {
    const attestationControl = form.get(this.selectedAttestationType);
    const insuredControl = form.get(this.isAttestationFor);
    if (!attestationControl || !insuredControl) {
      return "";
    }

    const attestationType = attestationControl.value as AttestationType;
    const insuredKey = insuredControl.value ? TRUE : FALSE;

    const translationKey = attestationTrameMapping[attestationType]?.[insuredKey];
    if (!translationKey) {
      return "";
    }

    return this.translate.instant(translationKey) || "";
  }

  validateTrameKeyDown(event: KeyboardEvent, form: FormGroup): void {
    const trame = this.getTrame(form);
    if (!trame) return;

    const inputElement = event.target as HTMLTextAreaElement;
    const selectionStart = inputElement.selectionStart;
    const protectedLength = TrameHelper.getProtectedLength(trame);

    if (
      (event.key === "Backspace" && selectionStart <= protectedLength) ||
      (event.key === "Delete" && selectionStart < protectedLength)
    ) {
      event.preventDefault();
    }
  }
}
